import React from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Card, Table, Tag, Typography, Statistic, Space, Button, Spin, Alert, Empty, notification } from 'antd';
import { FileDoneOutlined, FileProtectOutlined, DollarOutlined, UploadOutlined, PaperClipOutlined } from '@ant-design/icons';
import moment from 'moment';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { AttachmentsButton, AttachmentsPanel, formatBytes } from '../../components/AttachmentsPanel';
import { PdfButton } from '../../components/DocumentActions';
import { INVOICE_STATUS_COLORS } from '../main/InvoicesPage';
import { LETTER_STATUS_COLORS } from '../main/EngagementLettersPage';

const money = (v) => Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Client portal home (vision doc "Client Portal"): what the firm needs from
 * the client (document requests with upload, letters to sign), the engaged
 * services and coming deadlines, invoices and the account balance, files.
 */
export default function PortalHome() {
  const auth = useAuth();
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(() => {
    request
      .get('/portal/summary')
      .then((r) => r.json())
      .then((r) => (r.status ? setData(r.data) : setError(r.message)))
      .catch((e) => setError(e.message));
  }, []);
  React.useEffect(load, [load]);

  if (error) return <Alert type="error" showIcon message={error} style={{ maxWidth: 600 }} />;
  if (!data) return <Spin style={{ marginTop: 40 }} />;

  const pending = data.document_requests.filter((r) => r.status === 'PENDING');

  return (
    <div>
      <Typography.Title level={3} style={{ marginBottom: 0 }}>
        {data.client.client_name}
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        Client portal of {data.firm?.name}. Signed in as {auth.user.name}.
      </Typography.Paragraph>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic title="Documents requested" value={pending.length} prefix={<UploadOutlined />} valueStyle={{ color: pending.length ? '#d46b08' : undefined }} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic title="Letters awaiting your signature" value={data.letters_to_sign.length} prefix={<FileProtectOutlined />} valueStyle={{ color: data.letters_to_sign.length ? '#d46b08' : undefined }} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic title="Outstanding balance (EUR)" value={money(data.account.outstanding)} prefix={<DollarOutlined />} valueStyle={{ color: Number(data.account.outstanding) > 0 ? '#cf1322' : '#389e0d' }} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Documents your accountant needs" size="small" style={{ marginBottom: 16 }}>
            <Typography.Paragraph type="secondary">Upload the file against each request; it is marked as received automatically.</Typography.Paragraph>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={data.document_requests}
              locale={{ emptyText: 'Nothing requested' }}
              columns={[
                { title: 'Document', dataIndex: 'category', render: (v) => v.replace(/_/g, ' ') },
                { title: 'For', key: 'entity', render: (_, r) => r.Entity?.entity_name || '-' },
                { title: 'Due', dataIndex: 'due_date', render: (v) => v || '-' },
                { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={v === 'FULFILLED' ? 'green' : 'orange'}>{v === 'FULFILLED' ? 'RECEIVED' : 'PENDING'}</Tag> },
                { title: '', key: 'files', render: (_, r) => <AttachmentsButton ownerType="DOCUMENT_REQUEST" ownerId={r.id} title={`Upload - ${r.category.replace(/_/g, ' ')}`} onChange={load} canDelete={false} /> },
              ]}
            />
          </Card>

          <Card title="Engagement letters" size="small" style={{ marginBottom: 16 }}>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={data.letters}
              locale={{ emptyText: 'No engagement letters yet' }}
              columns={[
                { title: 'Letter', dataIndex: 'letter_number', render: (v, l) => <Link to={`/engagement-letters/${l.id}`}>{v}</Link> },
                { title: 'Title', dataIndex: 'title' },
                { title: 'Date', dataIndex: 'issue_date' },
                { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={LETTER_STATUS_COLORS[v]}>{v === 'SENT' ? 'AWAITING SIGNATURE' : v}</Tag> },
                {
                  title: '',
                  key: 'act',
                  render: (_, l) =>
                    l.status === 'SENT' ? (
                      <Link to={`/engagement-letters/${l.id}`}>
                        <Button size="small" type="primary" icon={<FileDoneOutlined />}>
                          Review & sign
                        </Button>
                      </Link>
                    ) : (
                      <PdfButton type="engagement-letter" id={l.id} size="small" children={null} />
                    ),
                },
              ]}
            />
          </Card>

          <Card title="Services and deadlines" size="small">
            {data.engagements.length === 0 ? (
              <Empty description="No active services" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  {data.engagements.map((e) => (
                    <Tag key={e.id} color="blue">
                      {e.Service?.service_name}
                      {e.Entity ? ` - ${e.Entity.entity_name}` : ''} ({(e.frequency || '').toLowerCase()})
                    </Tag>
                  ))}
                </div>
                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  dataSource={data.deadlines}
                  locale={{ emptyText: 'No upcoming deadlines' }}
                  columns={[
                    { title: 'Deadline', dataIndex: 'title', render: (v, d) => v || d.description || d.deadline_type || '-' },
                    { title: 'Due', dataIndex: 'due_date' },
                    { title: 'Status', dataIndex: 'status', render: (v) => <Tag>{v}</Tag> },
                  ]}
                />
              </Space>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title="Invoices"
            size="small"
            style={{ marginBottom: 16 }}
            extra={
              <Space>
                <Typography.Text type="secondary">Invoiced {money(data.account.invoiced)} - paid {money(data.account.paid)}</Typography.Text>
                <Link to="/portal/statement">Statement</Link>
              </Space>
            }
          >
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={data.invoices}
              locale={{ emptyText: 'No invoices yet' }}
              columns={[
                { title: 'Invoice', dataIndex: 'invoice_number', render: (v, i) => <Link to={`/invoices/${i.id}`}>{v}</Link> },
                { title: 'Date', dataIndex: 'invoice_date' },
                { title: 'Due', dataIndex: 'due_date' },
                { title: 'Total', dataIndex: 'total', align: 'right', render: money },
                { title: 'Balance', dataIndex: 'balance_due', align: 'right', render: (v) => <strong>{money(v)}</strong> },
                { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={INVOICE_STATUS_COLORS[v]}>{v.replace('_', ' ')}</Tag> },
                { title: '', key: 'pdf', render: (_, i) => <PdfButton type="invoice" id={i.id} size="small" children={null} /> },
              ]}
            />
          </Card>

          <Card title="Your entities" size="small" style={{ marginBottom: 16 }}>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={data.client.Entities || []}
              columns={[
                { title: 'Entity', dataIndex: 'entity_name', render: (v, e) => (e.is_primary ? <span>{v} <Tag color="blue">primary</Tag></span> : v) },
                { title: 'Type', dataIndex: 'entity_type' },
                { title: 'Reg. No.', dataIndex: 'registration_number', render: (v) => v || '-' },
                { title: 'VAT / TIN', dataIndex: 'tax_id', render: (v) => v || '-' },
              ]}
            />
          </Card>

          <Card title="Files" size="small">
            <Typography.Paragraph type="secondary">
              <PaperClipOutlined /> Recent: {data.recent_files.length ? data.recent_files.map((f) => `${f.original_name} (${formatBytes(f.size_bytes)}, ${moment(f.createdAt).format('YYYY-MM-DD')})`).join('; ') : 'none yet'}
            </Typography.Paragraph>
            <AttachmentsPanel ownerType="CLIENT" ownerId={data.client.id} categories={['GENERAL', 'KYC', 'CORRESPONDENCE', 'SUPPORTING_DOCUMENT']} canDelete={false} onChange={load} />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export function PortalStatement() {
  const auth = useAuth();
  const [statement, setStatement] = React.useState(null);
  React.useEffect(() => {
    if (!auth.user.clientId) return;
    request
      .get('/invoices/statement', { client_id: auth.user.clientId })
      .then((r) => r.json())
      .then((r) => (r.status ? setStatement(r.data) : notification.error({ message: r.message })));
  }, [auth.user.clientId]);
  if (!statement) return <Spin style={{ marginTop: 40 }} />;
  return (
    <div>
      <Typography.Title level={4}>Statement of Account</Typography.Title>
      <Space style={{ marginBottom: 12 }} size="large">
        <Typography.Text>Invoiced: <strong>{money(statement.totals.invoiced)}</strong></Typography.Text>
        <Typography.Text>Paid: <strong>{money(statement.totals.paid)}</strong></Typography.Text>
        {Number(statement.totals.credited) > 0 && <Typography.Text>Credited: <strong>{money(statement.totals.credited)}</strong></Typography.Text>}
        <Typography.Text type={statement.totals.outstanding > 0 ? 'danger' : 'success'}>
          Outstanding: <strong>{money(statement.totals.outstanding)}</strong>
        </Typography.Text>
      </Space>
      <Table
        rowKey={(e) => `${e.type}-${e.reference}`}
        size="small"
        pagination={false}
        dataSource={statement.entries}
        columns={[
          { title: 'Date', dataIndex: 'date' },
          { title: 'Type', dataIndex: 'type', render: (v) => <Tag color={{ INVOICE: 'blue', PAYMENT: 'green', CREDIT_NOTE: 'orange' }[v]}>{v.replace('_', ' ')}</Tag> },
          { title: 'Reference', key: 'ref', render: (_, e) => (e.type === 'INVOICE' ? <Link to={`/invoices/${e.invoice_id}`}>{e.reference}</Link> : e.reference) },
          { title: 'Description', dataIndex: 'description' },
          { title: 'Debit', dataIndex: 'debit', align: 'right', render: (v) => (v ? money(v) : '') },
          { title: 'Credit', dataIndex: 'credit', align: 'right', render: (v) => (v ? money(v) : '') },
          { title: 'Balance', dataIndex: 'balance', align: 'right', render: (v) => <strong>{money(v)}</strong> },
        ]}
      />
    </div>
  );
}
